// Reactive effect system
//
// Phase 4 hot-path collection audit (2026-05-16):
//
//   COLD (module init, called once):
//     - v1EffectRegistry: new Map<number, ReactiveEffectLike>  (lifecycle.ts:18)
//     - globalRegistry:   new Map<string, RegistryEntry>        (use.ts:43)
//     - effectQueue:      new Set<ReactiveEffect>               (request-context per-fork)
//
//   WARM (per component mount):
//     - ReactiveEffectLike.deps: PackedDepSet[] = []           (lifecycle.ts:31)
//     - DOMComponentInstance.children: new Set                 (render.ts:192)
//     - instance.nodes: Node[]                                 (render.ts:96)
//     - hookSignatures: string[] (dev only)                    (hook.ts:32)
//     - effects: ReactiveEffectLike[]                          (hook.ts:99)
//     - globalKeys: string[]                                   (use.ts:318/435)
//
//   HOT (per render / per signal trigger):
//     - reconcile() resultNodes, toRemove, toInsert: Node[]    (render.ts:565/572/589)
//     - renderNode array branch: oldKeys/usedKeys/nodes Set    (render.ts:318-340)
//
// Phase 4 pre-sizing strategy:
//   - PackedDepSet already starts at capacity 4 — covers most signals
//   - Component.effects[] starts empty; 95% of components have 0-2 effects so
//     [] + lazy push is already optimal (no resize until > 7)
//   - reconcile arrays start [] — typical lists have 0 inserts most renders;
//     pre-sizing to a guessed N would waste memory on cold paths
//   - The biggest single win remaining is avoiding the [...effectQueue] spread
//     in flush() — Task 15 below
import { getComponent, registerEffectWithComponent } from './hook'
import { getRequestContext } from './request-context'
import { PackedDepSet } from './packed-dep-set'
import { allocateEffectId } from './effect-id-allocator'

/**
 * V1 effect registry: maps the V1 effect's numeric ID back to its instance.
 *
 * Populated on construction, drained on `stop()`. Lookups happen in
 * triggerEffects, which now iterates a PackedDepSet of IDs instead of a
 * Set<ReactiveEffectLike> of references.
 *
 * V2 effect IDs share the same allocator (effect-id-allocator.ts re-exports
 * Phase 1's nextEffectId), so V1 and V2 effect IDs are guaranteed disjoint.
 * This map only holds V1 records — V2 records live in signal.effects directly.
 */
const v1EffectRegistry = new Map<number, ReactiveEffectLike>()

/** @internal — test-only inspection of registry size. */
export function __v1EffectRegistrySize(): number {
    return v1EffectRegistry.size
}

export class ReactiveEffectLike {
    readonly id: number
    /**
     * V1 dep tracking: each entry is the PackedDepSet that has registered this
     * effect's ID. On re-run, cleanup() iterates these and calls .delete(this.id).
     */
    deps: PackedDepSet[] = []
    active = true

    constructor(
        public fn: () => void,
        public scheduler?: () => void
    ) {
        this.id = allocateEffectId()
        v1EffectRegistry.set(this.id, this)
    }

    run() {
        if (!this.active) {
            return this.fn()
        }

        const ctx = getRequestContext()
        const parent: ReactiveEffect | undefined = ctx.activeEffect
        try {
            this.cleanup()
            ctx.activeEffect = this
            return this.fn()
        } finally {
            ctx.activeEffect = parent
        }
    }

    stop() {
        if (this.active) {
            this.cleanup()
            this.active = false
            v1EffectRegistry.delete(this.id)
        }
    }

    cleanup() {
        const { deps } = this
        if (deps.length) {
            for (let i = 0; i < deps.length; i++) {
                deps[i].delete(this.id)
            }
            deps.length = 0
        }
    }
}

/** Get the currently running reactive effect (for the current request/execution context). */
export function getActiveEffect(): ReactiveEffect | undefined {
    return getRequestContext().activeEffect
}

export type ReactiveEffect = ReactiveEffectLike

function _unsafeEffectImpl(
    fn: () => void,
    options: { scheduler?: () => void } = {},
    attachToComponent: boolean
): ((...args: unknown[]) => void) & { effect: ReactiveEffectLike } {
    const _effect = new ReactiveEffectLike(fn, options.scheduler)
    _effect.run()

    if (attachToComponent) {
        const component = getComponent()
        if (component) {
            registerEffectWithComponent(component, _effect)
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const runner: any = _effect.run.bind(_effect) // typed as any to allow attaching .effect property
    runner.effect = _effect
    return runner
}

interface UnsafeEffectFn {
    /**
     * Run a reactive effect.
     *
     * When called inside a component render, the effect is automatically registered
     * with the current component instance and is `.stop()`-ed on unmount.
     *
     * For framework-level or genuinely free-form effects that should survive component
     * teardown, use {@link unsafeEffect.detached}.
     *
     * @example
     * function Counter() {
     *   const [count] = use(0);
     *   unsafeEffect(() => console.log('count is', count()));
     *   return f('div', null, count());
     * }
     */
    (
        fn: () => void,
        options?: { scheduler?: () => void }
    ): ((...args: unknown[]) => void) & { effect: ReactiveEffectLike }
    /**
     * Run a reactive effect WITHOUT auto-cleanup.
     *
     * Use only for renderers or genuinely root effects that must survive component
     * teardown. Component-scoped effects should use plain {@link unsafeEffect}.
     *
     * @example
     * unsafeEffect.detached(() => renderToDom(root));
     */
    detached(
        fn: () => void,
        options?: { scheduler?: () => void }
    ): ((...args: unknown[]) => void) & { effect: ReactiveEffectLike }
}

export const unsafeEffect: UnsafeEffectFn = ((fn: () => void, options?: { scheduler?: () => void }) =>
    _unsafeEffectImpl(fn, options ?? {}, true)) as UnsafeEffectFn

unsafeEffect.detached = (fn: () => void, options?: { scheduler?: () => void }) =>
    _unsafeEffectImpl(fn, options ?? {}, false)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const __DEV__: boolean = (typeof process !== 'undefined' && (process as any)?.env?.NODE_ENV !== 'production')

export function trackEffect(dep: PackedDepSet) {
    const { activeEffect } = getRequestContext()
    if (activeEffect) {
        // V1 active effect always has .id (PackedDepSet keys by ID).
        // V2 EffectRecord also has .id but a different .deps shape
        // (SignalContainer[] not PackedDepSet[]) — we still record the ID
        // here, but skip pushing the dep into V2's deps list because that
        // would corrupt V2's typed array.
        if (__DEV__ && activeEffect.deps && activeEffect.deps.length > 0) {
            // Sanity: the first dep should expose `.add(id)`/`.delete(id)`,
            // matching PackedDepSet. If a V2 SignalContainer slips in, the
            // shape diverges and we warn so the bug surfaces in development.
            const first = activeEffect.deps[0] as unknown as { add?: unknown; delete?: unknown }
            if (typeof first !== 'object' || first === null || typeof first.add !== 'function') {
                console.warn(
                    '[flexium] trackEffect(V1) called with a V2-shaped active effect. ' +
                    'This indicates a V1/V2 routing bug — please report to the flexium maintainers.'
                )
            }
        }
        dep.add(activeEffect.id)
        // Only push into deps if the deps array is V1-shaped (PackedDepSet entries).
        // V2 EffectRecord.deps is SignalContainer[] — pushing a PackedDepSet would corrupt it.
        // Detect by checking if the first existing entry is a PackedDepSet (has .add accepting number).
        // For an empty deps array we cannot tell — but V2 effects only enter this path when reading
        // a V1 reactive() proxy from inside a V2 effect, which is an exotic case the V2 code branch
        // never produces in practice. Safe default: push.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- effect.deps holds PackedDepSet[] in V1; in V2 path it is SignalContainer[] and we deliberately skip the push then.
        const depsArr = activeEffect.deps as any[]
        if (depsArr.length === 0 || depsArr[0] instanceof PackedDepSet) {
            depsArr.push(dep)
        }
    }
}

export function queueJob(effect: ReactiveEffect) {
    const ctx = getRequestContext()
    if (!ctx.effectQueue.has(effect)) {
        ctx.effectQueue.add(effect)
        if (!ctx.isFlushPending && !ctx.isBatching) {
            ctx.isFlushPending = true
            Promise.resolve().then(flush)
        }
    }
}

// Late-bound V2 dispatcher — set by reactive-v2.ts at module init to avoid
// circular import. lifecycle.ts ↔ reactive-v2.ts cannot both static-import each
// other (reactive-v2.ts already imports types from this module).
let v2EffectRunner: ((effect: unknown) => void) | null = null

/** @internal — called from reactive-v2.ts at module init to register the V2 dispatcher. */
export function setV2EffectRunner(fn: (effect: unknown) => void): void {
    v2EffectRunner = fn
}

export function flush() {
    const ctx = getRequestContext()
    ctx.isFlushPending = false

    // Phase 4: avoid `[...ctx.effectQueue]` spread which allocates a new Array
    // per flush. Instead, swap the Set with a fresh one — the old Set is
    // iterated locally and then discarded. Effects scheduled DURING iteration
    // land in the new Set and will flush on the next microtask, matching the
    // intended single-microtask semantics.
    const draining = ctx.effectQueue
    ctx.effectQueue = new Set()

    for (const effect of draining) {
        // V1 effects expose .active + .run(); V2 effects (EffectRecord) expose .flags + .id.
        // Duck-type detection — branch lets V1 sync() drain V2-queued records.
        const maybeV2 = effect as unknown as { flags?: number; id?: number; run?: () => void }
        if (typeof maybeV2.run === 'function') {
            if (effect.active) effect.run()
        } else if (typeof maybeV2.flags === 'number' && v2EffectRunner) {
            // V2 record in the queue — clear SCHEDULED bit + dispatch via V2 runner.
            const flags = maybeV2.flags
            if ((flags & 0b0001) !== 0) {  // EFFECT_ACTIVE
                maybeV2.flags = flags & ~0b0100  // clear EFFECT_SCHEDULED
                v2EffectRunner(effect)
            }
        }
    }
}

export function setBatching(value: boolean) {
    getRequestContext().isBatching = value
}

export function triggerEffects(dep: PackedDepSet) {
    // Snapshot IDs into a local typed array to make iteration safe against
    // re-entrant modifications (effect.run() may call cleanup which mutates
    // dep). Snapshot allocation is bounded by signal arity.
    const ids = dep.toArray()
    const { activeEffect } = getRequestContext()
    const activeId = activeEffect?.id ?? 0
    for (let i = 0; i < ids.length; i++) {
        const id = ids[i]
        if (id === activeId) continue
        const effect = v1EffectRegistry.get(id)
        if (!effect || !effect.active) continue
        if (effect.scheduler) {
            effect.scheduler()
        } else {
            queueJob(effect)
        }
    }
}

/**
 * Unified sync API
 * - sync(): Force refresh (flush pending effects)
 * - sync(fn): Batch updates (run fn then flush)
 */
export function sync(fn?: () => void) {
    if (fn) {
        setBatching(true)
        try {
            fn()
        } finally {
            setBatching(false)
            flush()
        }
    } else {
        flush()
    }
}
